import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import TransportMenu from "./TransportMenu";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { scrollToTop } from "../Shared/ScrollOnTop";

/*
 * Drivers, their licences and the bus each is on.
 *
 * Drivers are staff: they live in tblStaff_Master under a Driver designation and are paid
 * through payroll like everyone else. Only what is specific to driving - the licence, and
 * which bus - is kept in the transport tables, so nothing here duplicates the employee
 * record.
 *
 * The point of the report is the licence column. A driver whose licence has run out is
 * carrying children on the road, and nothing else in the system would say so.
 */
function DriverReport() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [counts, setCounts] = useState({
    expired: 0,
    expiringSoon: 0,
    noLicence: 0,
    unassigned: 0,
  });
  const [activeOnly, setActiveOnly] = useState(true);

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      SchoolID: 1,
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const load = useCallback(() => {
    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "TransportReport/GetDrivers",
      headers: { "Content-Type": "application/json" },
      data: auth({ Is_Active: activeOnly }),
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setDrivers(res.data.ListofRecords || []);
          setCounts({
            expired: res.data.Expired || 0,
            expiringSoon: res.data.ExpiringSoon || 0,
            noLicence: res.data.NoLicence || 0,
            unassigned: res.data.Unassigned || 0,
          });
        } else {
          setDrivers([]);
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setLoading(false));
  }, [auth, activeOnly]);

  useEffect(() => {
    document.title = "Drivers & Licences";
    scrollToTop();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const licenceCell = (d) => {
    if (!d.Licence_No) {
      return <span className="badge bg-secondary text-white">not recorded</span>;
    }

    const days = d.LicenceDaysLeft;
    let badge = null;

    if (days === null || days === undefined) {
      badge = <span className="badge bg-secondary text-white ms-2">no expiry</span>;
    } else if (days < 0) {
      badge = (
        <span className="badge bg-danger text-white ms-2">expired {Math.abs(days)}d ago</span>
      );
    } else if (days <= 60) {
      badge = <span className="badge bg-warning text-dark ms-2">{days}d left</span>;
    }

    return (
      <>
        {d.Licence_No}
        {d.Licence_Type ? (
          <span className="text-muted small"> ({d.Licence_Type})</span>
        ) : null}
        {badge}
      </>
    );
  };

  const renderRows = () => {
    if (drivers.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="text-center text-muted py-4">
            No drivers found
          </td>
        </tr>
      );
    }

    return drivers.map((d, i) => (
      <tr key={d.Staff_ID}>
        <td>{i + 1}</td>
        <td className="fw-bold">{d.Name}</td>
        <td>{d.FatherName || "—"}</td>
        <td>{d.Mobile_No || "—"}</td>
        <td>{licenceCell(d)}</td>
        <td>
          {d.Licence_Expiry ? moment(d.Licence_Expiry).format("DD-MMM-YYYY") : "—"}
        </td>
        <td>
          {d.Registration_No ? (
            <span className="fw-bold">{d.Registration_No}</span>
          ) : (
            <span className="text-muted fst-italic">not assigned</span>
          )}
        </td>
        <td>
          <span className={`badge text-white ${d.Is_Active ? "bg-success" : "bg-secondary"}`}>
            {d.Is_Active ? "Active" : "Inactive"}
          </span>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <TransportMenu />
      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-7">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Transport/TransportDashboard">Transport</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Drivers
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Drivers &amp; Licences</div>
                <p className="text-muted small mb-0">
                  Drivers are staff records — pay and personal details stay in the employee
                  module. Only the licence and the bus are kept here.
                </p>
              </div>
              <div className="col-md-5">
                <div className="page-right-actions">
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="activeOnly"
                      checked={activeOnly}
                      onChange={(e) => setActiveOnly(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="activeOnly">
                      Serving drivers only
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Drivers</div>
                    <div className="h4 mb-0">{drivers.length}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Licence expired</div>
                    <div
                      className={`h4 mb-0 ${counts.expired > 0 ? "text-danger" : ""}`}
                    >
                      {counts.expired}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Expiring within 60 days</div>
                    <div className="h4 mb-0">{counts.expiringSoon}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Licence not recorded</div>
                    <div className="h4 mb-0">{counts.noLicence}</div>
                    <div className="small text-muted">
                      {counts.unassigned} on no bus
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-theme">
                    <thead>
                      <tr>
                        <th>Sr.</th>
                        <th>Driver</th>
                        <th>Father Name</th>
                        <th>Mobile</th>
                        <th>Licence</th>
                        <th>Expires</th>
                        <th>Bus</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : (
                        renderRows()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DriverReport;
